<template>
  <div>
    <!-- Header -->
    <div
      class="flex flex-col-xs-only flex-wrap justify-space-between align-items-center p-y-m p-l-sm-xs-only p-r-n-xs-only"
    >
      <h1 class="font-heavy m-t-none m-b-none">Locations</h1>
      <div
        v-if="allLocations.length > 0"
        class="flex gap-xs align-self-flex-start-xs-only m-t-sm-xs-only"
      >
        <a :href="newUrl" class="btn btn-primary btn-compact">
          <i class="fa-solid fa-plus m-r-xxs"></i> Create Location
        </a>
      </div>
    </div>

    <!-- "Should you use locations?" decision guide. Replaces the old alert
         banners (too easy to skim past) and doubles as the empty state: a
         structured card that walks coaches through who this is for. Rows build
         to the qualifying case last — single location = skip, separate
         entities = separate account, one entity = use it — where the zero-state
         Create Location CTA lives so the action sits with the answer. -->
    <div class="border border-solid border-gray-200 rounded-md m-b-md bg-gray-50">
      <div class="p-x-md p-y-sm border-bottom">
        <span class="font-semi text-14 text-gray-700">Should you use locations?</span>
      </div>

      <div class="flex align-items-start gap-sm p-x-md p-y-sm">
        <span
          class="flex align-items-center justify-center flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 text-gray-500"
        >
          <i class="fa-solid fa-ban"></i>
        </span>
        <div>
          <div class="flex align-items-center gap-xs flex-wrap">
            <span class="font-semi text-14 text-gray-900">One location only</span>
            <span class="label label-default">Skip this feature</span>
          </div>
          <div class="text-14 text-gray-600 m-t-xxs">
            Locations only help clubs that run out of more than one place.
            Nothing here changes how your club works — you can leave this page.
          </div>
        </div>
      </div>

      <div class="flex align-items-start gap-sm p-x-md p-y-sm border-top">
        <span
          class="flex align-items-center justify-center flex-shrink-0 w-8 h-8 rounded-full bg-blue-50 text-blue-600"
        >
          <i class="fa-regular fa-building"></i>
        </span>
        <div>
          <div class="flex align-items-center gap-xs flex-wrap">
            <span class="font-semi text-14 text-gray-900">
              Multiple locations, separate legal entities
            </span>
            <span class="label label-info">Separate WrestlingIQ account</span>
          </div>
          <div class="text-14 text-gray-600 m-t-xxs">
            Don't add them here.
            <a href="https://www.wrestlingiq.com/activate" class="text-underline"
              >Set up a separate WrestlingIQ account</a
            >
            for each entity — this keeps payments, taxes, and reporting tied to
            the right business.
          </div>
        </div>
      </div>

      <div class="flex align-items-start gap-sm p-x-md p-y-sm border-top">
        <span
          class="flex align-items-center justify-center flex-shrink-0 w-8 h-8 rounded-full bg-green-50 text-green-600"
        >
          <i class="fa-solid fa-check"></i>
        </span>
        <div>
          <div class="flex align-items-center gap-xs flex-wrap">
            <span class="font-semi text-14 text-gray-900">
              Multiple locations, one legal entity
            </span>
            <span class="label label-success">Use this feature</span>
          </div>
          <div class="text-14 text-gray-600 m-t-xxs">
            One LLC, non-profit, etc.? You're in the right place — add each
            location, then assign rosters and sessions to it to filter your
            calendar, rosters, and reports by location.
          </div>
          <a
            v-if="allLocations.length === 0"
            :href="newUrl"
            class="btn btn-primary btn-sm m-t-sm"
          >
            <i class="fa-solid fa-plus m-r-xxs"></i> Create your first location
          </a>
        </div>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger">
      {{ errorMessage }}
    </div>

    <div v-if="allLocations.length > 0" class="flex flex-col">
      <div
        v-for="location in allLocations"
        :key="location.id"
        class="flex justify-space-between align-items-center gap-sm p-y-sm border-top border-gray-200"
      >
        <div style="min-width: 0">
          <div class="flex align-items-center gap-xs flex-wrap">
            <span class="font-semi text-16 text-gray-900">{{
              location.name
            }}</span>
            <span v-if="location.archived" class="label label-default"
              >Archived</span
            >
            <span v-if="location.time_zone" class="label label-info" data-cy="location-time-zone-badge">
              <i class="fa fa-globe"></i> {{ zoneName(location.time_zone) }}
            </span>
          </div>
          <div
            v-if="addressSummary(location)"
            class="text-14 text-gray-600 m-t-xxs"
          >
            {{ addressSummary(location) }}
          </div>
          <div
            v-if="registrationLinkFor(location)"
            class="flex align-items-center gap-x-xs text-14 m-t-xxs hidden-xs hidden-sm"
          >
            <a
              :href="registrationLinkFor(location)"
              class="text-gray-700 break-words"
              >{{ registrationLinkFor(location) }}</a
            >
            <copy-icon
              :text="registrationLinkFor(location)"
              class="flex-shrink-0"
            ></copy-icon>
          </div>
        </div>
        <div class="flex gap-xs flex-shrink-0">
          <copy-icon
            v-if="registrationLinkFor(location)"
            class="btn btn-sm btn-default hidden-md hidden-lg"
            :text="registrationLinkFor(location)"
            label="Sign up link"
          ></copy-icon>
          <a :href="editUrl(location)" class="btn btn-sm btn-default">
            <i class="fa-solid fa-pencil m-r-xxs"></i> Edit
          </a>
          <button
            class="btn btn-sm btn-default text-red-600"
            @click="confirmDelete(location)"
            aria-label="Delete location"
          >
            <i class="fa-solid fa-trash-can"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteConfirm"
      class="modal fade in"
      style="display: block"
      @click.self="cancelDelete"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <button type="button" class="close" @click="cancelDelete">
              &times;
            </button>
            <h4 class="modal-title">Delete Location</h4>
          </div>
          <div class="modal-body">
            <p>
              Are you sure you want to delete
              <strong>{{
                locationToDelete ? locationToDelete.name : ""
              }}</strong
              >?
            </p>
            <p class="text-gray-600">
              Rosters and sessions assigned to this location will keep working,
              but will no longer be linked to a location.
            </p>
          </div>
          <div class="modal-footer">
            <button class="btn btn-default" @click="cancelDelete">
              Cancel
            </button>
            <ladda-button
              @lbClicked="deleteLocation"
              el-class="btn-danger"
              :loading="deleting"
            >
              Delete
            </ladda-button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showDeleteConfirm" class="modal-backdrop fade in"></div>
  </div>
</template>

<script>
import LaddaButton from '../../shared/ladda_button.vue';
import CopyIcon from './copy_icon.vue';
import { errorableMix } from './mixins/errorable_mix';
import { zoneLabel } from '../event_time_zone';

export default {
  name: 'locations-list',
  components: { CopyIcon, LaddaButton },
  mixins: [errorableMix],
  props: {
    locations: {
      type: Object,
      required: true,
    },
    newUrl: {
      type: String,
      required: true,
    },
    registrationBaseUrl: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      allLocations: [],
      showDeleteConfirm: false,
      locationToDelete: null,
      deleting: false,
    };
  },
  mounted() {
    if (this.locations && this.locations.locations) {
      this.allLocations = this.locations.locations;
    }
  },
  methods: {
    zoneName(zone) {
      return zoneLabel(zone);
    },
    addressSummary(location) {
      const state = location.state ? location.state.toUpperCase() : '';
      const cityState = [location.city, state].filter(Boolean).join(', ');
      return [location.address_line1, cityState].filter(Boolean).join(' · ');
    },
    editUrl(location) {
      return `/settings/team/locations/${location.id}/edit`;
    },
    registrationLinkFor(location) {
      if (!this.registrationBaseUrl || !location.slug || location.archived) {
        return null;
      }

      return `${this.registrationBaseUrl}/location/${location.slug}`;
    },
    confirmDelete(location) {
      this.locationToDelete = location;
      this.showDeleteConfirm = true;
    },
    cancelDelete() {
      this.locationToDelete = null;
      this.showDeleteConfirm = false;
    },
    async deleteLocation() {
      if (!this.locationToDelete) return;

      this.deleting = true;

      try {
        await axios.delete(
          this.$apiService.locationUrl(this.locationToDelete.id),
        );

        const idx = this.allLocations.findIndex(
          (l) => l.id === this.locationToDelete.id,
        );
        if (idx > -1) {
          this.allLocations.splice(idx, 1);
        }

        this.cancelDelete();
      } catch (error) {
        this.showError(this.parseErrorResponse(error));
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>
